// src/config/siteText.js
// DEFAULT_FEATURES: migrated verbatim from prototype lines 5416-5423.
// DEFAULT_CONTACT_LINKS: migrated verbatim from prototype lines 5424-5427.
// DEFAULT_SITE_TEXT: migrated verbatim from prototype lines 5456-5504.
// (Forward-ref order: DEFAULT_FEATURES + DEFAULT_CONTACT_LINKS declared first
// since DEFAULT_SITE_TEXT references them at lines 5472 + 5474.)
// Sprint 2b-1 amend: FEATURE_ICON_KEYS added (prototype line 5415).

import { ABOUT_COPY, CIE_NOTE, HERO_TAGLINE, BOARDS_ANSWER, INDEPENDENCE_QUESTION, INDEPENDENCE_ANSWER } from '../components/home/marketingCopy.js';
import { AI_OFFER_COPY, FREE_SAMPLE_ANSWER, AI_MARKING_ANSWER } from '../components/home/offerCopy.js';
import { PAYMENT_FAQ, ACCESS_FAQ } from './paymentCopy.js';
import { HOME_HERO, PARENTS_POINTS, PARENTS_HEADING, PARENTS_HEADING_ZH, REVISION_FEATURES } from '../components/home/homeExperience.js';
import { HERO_IMAGE_PLACEHOLDER } from './homeImages.js';

export const FEATURE_ICON_KEYS = ['feather', 'headphones', 'cards', 'question', 'message', 'edit'];

export const DEFAULT_FEATURES = [
    ...REVISION_FEATURES,
    { title: "AI Study Partner", text: "Ask about your pack and explore ideas with replies in your preferred language." },
    { title: "Notes & Bookmarks", text: "Write beside the text. Export to PDF. Your study journal travels with you." }
];

export const DEFAULT_CONTACT_LINKS = [
    { iconKey: 'mail', label: 'Email', display: 'learn@daniellenglish.com', url: 'mailto:learn@daniellenglish.com' },
    { iconKey: 'instagram', label: 'Instagram', display: '@danielles.tsundoku', url: 'https://www.instagram.com/danielles.tsundoku' }
];

// home-packfinder: the "Find your pack" wizard. EVERYTHING the visitor reads -- the
// questions, the options, which pack each combination recommends and why -- is data
// here, so Danielle re-maps it in the Site Text admin tab when the catalogue changes
// and no deploy is needed. Nothing about packs, papers or prices lives in JSX.
//
// HOW AN OUTCOME IS CHOSEN (src/components/home/packFinder.js resolveOutcome):
//   - `when` lists the answers an outcome needs, keyed by question id. A BLANK value
//     means "any answer". All three keys ship on every outcome -- the admin editor
//     can fill in a blank but cannot add a missing key.
//   - Among the outcomes that match, the one with the MOST filled-in conditions wins;
//     a tie goes to the one listed first. So order still runs specific -> general, but
//     an outcome Danielle ADDS (the editor appends it at the end, after the catch-all)
//     is not dead on arrival.
//   - The outcome with every condition blank is the CATCH-ALL. It must exist.
//   - `packIds` are live /packs document ids. An id that is not in the live catalogue
//     is skipped silently, so retiring a pack never breaks the wizard.
//
// The copy states only what the packs themselves state (mark totals come from the 4EA1
// specification as used throughout the packs). No grades promised, no statistics.
// It must also pass the save CF's content guard (functions/src/admin/contentAdmin.js
// DANGEROUS) -- a failing string here would block every Site Text save, so
// packFinder.test.js asserts it.
export const DEFAULT_PACK_FINDER = {
    enabled: true,
    heading: "Not sure where to start?",
    subheading: "Three questions and we'll point you at the right pack.",
    ctaLabel: "Find my pack",
    restartLabel: "Start again",
    backLabel: "Back",
    resultLabel: "Where we'd start",
    tryFreeLabel: "Try a free lesson",
    seeInsideLabel: "See what's inside",
    libraryLabel: "Go to your library",
    seeAllLabel: "See all packs",
    questions: [
        {
            id: 'board',
            label: "Which exam board are you sitting?",
            options: [
                { value: 'edexcel', label: "Edexcel International GCSE English Language A (4EA1)" },
                { value: 'oxfordaqa', label: "OxfordAQA International GCSE English Literature (9275)" },
                { value: 'cie', label: 'Cambridge IGCSE (CIE) — coming soon' },
                { value: 'unsure', label: "I'm not sure yet" }
            ]
        },
        {
            id: 'focus',
            label: "What do you most need to fix?",
            options: [
                { value: 'reading', label: "Reading and analysing non-fiction texts" },
                { value: 'transactional', label: "Writing articles, speeches and letters" },
                { value: 'poetry-prose', label: "The poetry and prose anthology texts" },
                { value: 'imaginative', label: "Story and descriptive writing" },
                { value: 'literature', label: "A Shakespeare play (Macbeth)" },
                { value: 'exam', label: "Timing, exam technique and full mock papers" },
                { value: 'unsure', label: "I'm not sure yet" }
            ]
        },
        {
            id: 'target',
            label: "Where are you now?",
            options: [
                { value: 'starting', label: "Just starting the course" },
                { value: 'improving', label: "Part-way through, and my marks are stuck" },
                { value: 'final', label: "The exam is close" }
            ]
        }
    ],
    outcomes: [
        // ---- OxfordAQA: one pack today, whatever the focus ----
        {
            when: { board: 'oxfordaqa', focus: '', target: '' },
            packIds: ['aqa-macbeth'],
            title: "Start with the Macbeth literature pack",
            why: "Macbeth is our OxfordAQA International GCSE English Literature (9275) pack. It works through the play scene by scene, then builds the character, theme and quotation work an essay needs, with annotated model essays aligned to the assessment objectives."
        },
        { when: { board: 'cie', focus: '', target: '' }, packIds: [], title: CIE_NOTE,
            why: 'Cambridge content is in preparation. The current Edexcel and OxfordAQA packs follow their own specifications. Please check back for Cambridge pack details before choosing.' },
        // ---- Edexcel, exam is close: the focus pack plus the mocks ----
        {
            when: { board: 'edexcel', focus: 'reading', target: 'final' },
            packIds: ['edexcel-4ea1-anthology', 'edexcel-4ea1-exam-mastery-original-mocks'],
            title: "The Non-Fiction Anthology, then the mock papers",
            why: "Paper 1 Section A is 45 of Paper 1's 90 marks, and the anthology text in it is one you can prepare in advance. Secure those texts first, then use the original mock papers to practise the whole section against the clock."
        },
        {
            when: { board: 'edexcel', focus: 'transactional', target: 'final' },
            packIds: ['edexcel-4ea1-transactional-writing', 'edexcel-4ea1-exam-mastery-original-mocks'],
            title: "Transactional Writing, then the mock papers",
            why: "Paper 1 Section B is a single 45-mark piece of writing. Learn the forms and how the marks are awarded, then sit the original mock papers to practise finishing it in 45 minutes."
        },
        {
            when: { board: 'edexcel', focus: 'poetry-prose', target: 'final' },
            packIds: ['edexcel-4ea1-poetry-prose', 'edexcel-4ea1-exam-mastery-original-mocks'],
            title: "The Poetry and Prose bundle, then the mock papers",
            why: "Paper 2 Section A asks about one of ten anthology texts, so every one of them has to be ready. The bundle covers all five poems and all five prose texts; the mock papers then rehearse the Paper 2 essay against the clock, on original texts."
        },
        {
            when: { board: 'edexcel', focus: 'imaginative', target: 'final' },
            packIds: ['edexcel-4ea1-imaginative-writing', 'edexcel-4ea1-exam-mastery-original-mocks'],
            title: "Imaginative Writing, then the mock papers",
            why: "Paper 2 Section B is 30 marks for one piece of imaginative writing in 45 minutes. Learn to plan and control a story at that length, then rehearse it in the original mock papers."
        },
        // ---- Edexcel, by focus ----
        {
            when: { board: 'edexcel', focus: 'reading', target: '' },
            packIds: ['edexcel-4ea1-anthology'],
            title: "Start with the Non-Fiction Anthology",
            why: "Paper 1 Section A is 45 of Paper 1's 90 marks, and one of its texts comes from the anthology you can study in advance. This pack takes every anthology text apart for language and structure, with banded model answers for the analysis and comparison questions."
        },
        {
            when: { board: 'edexcel', focus: 'transactional', target: '' },
            packIds: ['edexcel-4ea1-transactional-writing'],
            title: "Start with Transactional Writing",
            why: "Paper 1 Section B is one 45-mark piece: an article, a speech, a letter, a guide, a review or a leaflet. This pack teaches each form, how to plan it quickly, and what separates a secure answer from a sophisticated one."
        },
        {
            when: { board: 'edexcel', focus: 'poetry-prose', target: '' },
            packIds: ['edexcel-4ea1-poetry-prose'],
            title: "Start with the Poetry and Prose bundle",
            why: "Paper 2 Section A is 30 marks on a single anthology text, and you will not know which one until you open the paper. The bundle covers all five poems and all five prose texts, so there is no text you hope will not come up."
        },
        {
            when: { board: 'edexcel', focus: 'imaginative', target: '' },
            packIds: ['edexcel-4ea1-imaginative-writing'],
            title: "Start with Imaginative Writing",
            why: "Paper 2 Section B is 30 marks for one story or description written in 45 minutes. This pack covers the decisions that shape it — which task to pick, where to begin, viewpoint, dialogue and the ending — and the accuracy marks that are easy to lose."
        },
        {
            when: { board: 'edexcel', focus: 'exam', target: '' },
            packIds: ['edexcel-4ea1-exam-mastery-original-mocks'],
            title: "Start with Exam Mastery and the original mocks",
            why: "If you know the content but lose marks to timing, misread questions or unfinished answers, this is the pack. It maps both papers, decodes the command words, and gives you three sets of original mock papers with full model scripts."
        },
        {
            when: { board: 'edexcel', focus: 'unsure', target: '' },
            packIds: ['edexcel-4ea1-exam-mastery-original-mocks'],
            title: "Start with the whole-exam map",
            why: "When you cannot tell where the marks are going, begin with the pack that covers the whole qualification. Its route map and diagnostics show which paper and which question type is costing you most, and that tells you what to study next."
        },
        {
            when: { board: 'edexcel', focus: 'literature', target: '' },
            packIds: ['aqa-macbeth'],
            title: "Macbeth is an OxfordAQA Literature pack",
            why: "Edexcel English Language A (4EA1) has no Shakespeare paper, so check with your teacher which exam Macbeth is for. Our Macbeth pack is written to the OxfordAQA Literature (9275) mark scheme: the reading of the play carries across boards, but the exam guidance is specific to that one."
        },
        // ---- Board not known yet: go by the skill, and say which board it is for ----
        {
            when: { board: '', focus: 'literature', target: '' },
            packIds: ['aqa-macbeth'],
            title: "Start with the Macbeth literature pack",
            why: "Macbeth is our OxfordAQA International GCSE English Literature (9275) pack. If you are not sure that is your board, ask your teacher — the paper code is on your exam entry."
        },
        {
            when: { board: '', focus: 'reading', target: '' },
            packIds: ['edexcel-4ea1-anthology'],
            title: "The Non-Fiction Anthology is the one to look at",
            why: "This pack is written for Edexcel International GCSE English Language A (4EA1). Before you unlock it, check the paper code on your exam entry — the free lessons are open to everyone in the meantime."
        },
        {
            when: { board: '', focus: 'transactional', target: '' },
            packIds: ['edexcel-4ea1-transactional-writing'],
            title: "Transactional Writing is the one to look at",
            why: "This pack is written for Edexcel International GCSE English Language A (4EA1). Before you unlock it, check the paper code on your exam entry — the free lessons are open to everyone in the meantime."
        },
        {
            when: { board: '', focus: 'poetry-prose', target: '' },
            packIds: ['edexcel-4ea1-poetry-prose'],
            title: "The Poetry and Prose bundle is the one to look at",
            why: "The bundle covers the ten Paper 2 anthology texts for Edexcel International GCSE English Language A (4EA1). Check the paper code on your exam entry before you unlock it — the free lessons are open to everyone in the meantime."
        },
        {
            when: { board: '', focus: 'imaginative', target: '' },
            packIds: ['edexcel-4ea1-imaginative-writing'],
            title: "Imaginative Writing is the one to look at",
            why: "This pack is written for Edexcel International GCSE English Language A (4EA1). Before you unlock it, check the paper code on your exam entry — the free lessons are open to everyone in the meantime."
        },
        // ---- CATCH-ALL: every condition blank. Must exist. ----
        {
            when: { board: '', focus: '', target: '' },
            packIds: [],
            title: "Find out your exam board first",
            why: "The packs are written to a specific exam, so the one thing worth knowing is your board. Ask your teacher, or look at your exam entry: Edexcel English Language A papers are coded 4EA1, and OxfordAQA English Literature is 9275. Until then, open any pack below — the free lessons need no account."
        }
    ]
};

// home-packfinder: the FAQ, shown on the home page and on the prerendered /faq page
// (where it also becomes FAQPage structured data). Plain text -- no markdown, no HTML.
// Every answer restates something the site already says elsewhere; none quotes a
// price, because prices are Danielle-editable on the pack and would go stale here.
export const DEFAULT_FAQ = {
    enabled: true,
    heading: "Common questions",
    items: [
        {
            q: "Which exam boards are the packs written for?",
            a: BOARDS_ANSWER
        },
        {
            q: "Which pack should I start with?",
            a: "Answer the three questions under \"Not sure where to start?\" on the home page and it will point you at one. As a rule: start with the part of the paper where you lose the most marks, and add the mock papers when the exam is close."
        },
        {
            q: "Can I try a pack before I buy it?",
            a: FREE_SAMPLE_ANSWER
        },
        {
            q: "How long do I keep access?",
            a: ACCESS_FAQ
        },
        {
            q: "How do I pay?",
            a: PAYMENT_FAQ
        },
        {
            q: "What does the AI marking actually do?",
            a: AI_MARKING_ANSWER
        },
        {
            q: INDEPENDENCE_QUESTION,
            a: INDEPENDENCE_ANSWER
        },
        {
            q: "Can I get one-to-one help as well?",
            a: "Yes. Once you have an account you can request a session from the Booking page, and Danielle also reviews university personal statements — see the Personal Statement page."
        }
    ]
};

export const DEFAULT_SITE_TEXT = {
    heroTagline: HERO_TAGLINE,
    heroTitle: HOME_HERO.title,
    heroSubtitle: "with Danielle",
    heroDescription: HOME_HERO.description,
    heroBtnLoggedIn: "Enter Study Packs",
    heroBtnLoggedOut: "Begin Your Journey",
    heroBtnMeet: "Meet Danielle",
    // Home intro video (Slice 3). Empty by default -> the hero shows no video block.
    // Accepts a full YouTube URL (watch?v= / youtu.be/ / /embed/) or a bare 11-char id.
    homeIntroVideoUrl: '',
    heroGallery: [{ imageUrl: HERO_IMAGE_PLACEHOLDER, caption: '', imageUrlFrame: { x: 50, y: 20, zoom: 1 } }],
    parentsHeading: PARENTS_HEADING,
    parentsHeadingZh: PARENTS_HEADING_ZH,
    parentsVideoUrl: '',
    parentsPoints: PARENTS_POINTS,
    // "Feel the AI" pre-signup demo copy (HomeAiPreview). Board-NEUTRAL defaults;
    // admin-editable via the Site Text tab (RecursiveEditor renders these keys
    // automatically). HomeAiPreview falls back to these strings if a field is blank.
    aiDemoHeading: 'Get unstuck. Write. Improve.',
    aiDemoSubheading: AI_OFFER_COPY.subheading,
    // Own-essay marking is entitlement-bound (a paid-pack feature) -- the copy
    // must NOT imply free own-essay marking. The free part is the on-us demo.
    aiDemoCta: AI_OFFER_COPY.cta,
    // home-4b-proof: the ONE new key of that slice. The demo's "after" paragraph --
    // the same student sentence, rewritten. Blank -> the shipped sample in
    // HomeAiPreview stands, so the demo is complete out of the box; filled ->
    // Danielle's rewrite wins with no deploy. The "before" paragraph and the
    // callouts stay in code beside it, so the default pair always corresponds.
    // IT IS A DEMONSTRATION, not a real student's marked work, and the section
    // says so on screen.
    aiDemoAfter: '',
    // home-packfinder: the "Find your pack" wizard and the FAQ (defined above). Both
    // are whole-object defaults: the live bridge REPLACES state.siteText with the saved
    // doc, so until Danielle next saves Site Text these keys are absent on the live
    // site and the components fall back to these defaults (resolvePackFinder /
    // resolveFaq). The admin form seeds them under the live doc, so she sees and can
    // edit them straight away.
    packFinder: DEFAULT_PACK_FINDER,
    faq: DEFAULT_FAQ,
    // Personal Statement service (personal-statement slice). Danielle fills
    // these live via the Site Text admin tab (RecursiveEditor renders new keys
    // automatically -- no deploy needed). indicativePrice/feeNote are
    // DISPLAY-ONLY (the actual fee is quoted per request, never charged here).
    // The copy keys are BLANK by default -> the page falls back to the shipped
    // copy in src/config/personalStatement.js (also the prerender/SSG source);
    // FILLED -> Danielle's text wins on both the live page and the next build's
    // prerendered HTML. Blank price degrades to "Fees are quoted per request."
    personalStatement: {
        indicativePrice: '',
        feeNote: '',
        eyebrow: '',
        title: '',
        lede: '',
        proofEyebrow: '',
        proofLine: '',
        reviewTitle: '',
        reviewBody: '',
        consultationTitle: '',
        consultationBody: '',
        disclaimer: ''
    },
    // About Danielle
    aboutRole: "English Educator · IGCSE Specialist",
    aboutName: "Ms. Danielle Lam",
    aboutPara1: ABOUT_COPY.para1,
    aboutPara2: ABOUT_COPY.para2,
    aboutQuote: '',
    profilePhotoUrl: '',
    // What's Inside — 6 feature cards
    features: DEFAULT_FEATURES,
    // Get in Touch — contact links
    contactLinks: DEFAULT_CONTACT_LINKS,
    // Footer
    footerYear: '2026',
    footerCopyrightText: 'Between the Lines with Danielle. All rights reserved.',
    // Photos Carousel (Feature 7) — admin uploads, default hidden
    photosCarouselEnabled: false,
    photosCarouselTitle: 'Glimpses of the Classroom',
    photosCarouselSubtitle: 'Moments from teaching, learning, and the literature we love.',
    photos: [
        { id: 'p1', imageUrl: '', caption: '' },
        { id: 'p2', imageUrl: '', caption: '' },
        { id: 'p3', imageUrl: '', caption: '' },
        { id: 'p4', imageUrl: '', caption: '' },
        { id: 'p5', imageUrl: '', caption: '' },
        { id: 'p6', imageUrl: '', caption: '' },
        { id: 'p7', imageUrl: '', caption: '' },
        { id: 'p8', imageUrl: '', caption: '' }
    ],
    // Testimonials Carousel (Feature 7) — admin uploads, default hidden
    testimonialsEnabled: false,
    testimonialsTitle: 'Voices from Students',
    testimonialsSubtitle: 'What learners say about studying with Danielle.',
    testimonials: [
        { id: 't1', studentName: '', studentContext: '', quote: '', photoUrl: '' },
        { id: 't2', studentName: '', studentContext: '', quote: '', photoUrl: '' },
        { id: 't3', studentName: '', studentContext: '', quote: '', photoUrl: '' },
        { id: 't4', studentName: '', studentContext: '', quote: '', photoUrl: '' },
        { id: 't5', studentName: '', studentContext: '', quote: '', photoUrl: '' },
        { id: 't6', studentName: '', studentContext: '', quote: '', photoUrl: '' }
    ]
};
